import { useEffect, useRef } from 'react';

/**
 * Mouse click-and-drag horizontal scrolling for an `overflow-x-auto` element.
 * Native scroll already handles wheel/trackpad/touch; this only adds the
 * grab-drag gesture that browsers don't provide. Mouse pointers only, so it
 * never fights touch momentum. A drag past a small threshold suppresses the
 * trailing click, so releasing over a button doesn't fire it.
 */
export function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || e.pointerType !== 'mouse') return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
    };

    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        el.classList.add('cursor-grabbing');
      }
      if (moved) el.scrollLeft = startLeft - dx;
    };

    const onUp = () => {
      if (!down) return;
      down = false;
      el.classList.remove('cursor-grabbing');
      if (moved) {
        const cancel = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        el.addEventListener('click', cancel, { capture: true, once: true });
      }
    };

    const onDragStart = (e: Event) => e.preventDefault();

    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    el.addEventListener('dragstart', onDragStart);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      el.removeEventListener('dragstart', onDragStart);
    };
  }, []);

  return ref;
}
